import {Deal, PaymentCurrencyType} from "@irati/types"
import {QueryHookOptions} from "@irati/utils/services/apollo"
import React from "react"
import {usePointsBlendQuery} from "@irati/wallet/hooks/pointsBlend"
import {isFlexiCurrency} from "@shared/wallet"
import {useWalletCashQuery} from "@irati/wallet/hooks/wallet"
import getCurrencyForDeal from "../helpers/deals/currency"

const dealContext = React.createContext<null | Deal>(null)

/** NOTE: Implementation details:
 *
 * helper methods should find there way in here for easy access and
 * discoverability.
 *
 * Ideally the deal entity should make most of this information available,
 * however with the current uncertainty of points blend and catalogue blend,
 * this hook allows the wallet information to be considered (as the BE has no
 * concept of a ‘blended’ wallet).
 *
 * The deals code is spread far and wide throughout the codebase, and in an
 * ideal world this context will become a source of simple truth for all things
 * ‘deals’, e.g how to display the price should be exposed here as fields, not
 * derived in components; that said there is a lot of legacy ground to cover so
 * for now it’s best to leave existing code as it is until we have a more
 * robust regression suite.
 */
export function DealContextProvider({deal, children}: {deal: Deal; children: React.ReactNode}) {
  return <dealContext.Provider value={deal}>{children}</dealContext.Provider>
}

interface Props extends Pick<QueryHookOptions, "fetchPolicy"> {}

export function useDeal({fetchPolicy}: Props = {}) {
  const deal = React.useContext(dealContext)
  const {data, load} = usePointsBlendQuery({fetchPolicy})

  if (!deal) {
    throw new Error("useDeal must be used inside dealContext")
  }

  const showPointsBlend = !!data?.walletBlend.visible
  const dealCurrency = getCurrencyForDeal(deal)
  const isPointsPurchase = isFlexiCurrency(dealCurrency)
  const isPointsBlendDeal = isPointsPurchase && showPointsBlend
  const cashWalletData = useCashWallet({fetchPolicy, isPointsBlendDeal})

  return React.useMemo(() => {
    const displayCurrency = isPointsBlendDeal ? data?.walletBlend.currency : (dealCurrency as PaymentCurrencyType)
    const cashWallet = cashWalletData?.availableBalanceInCents
    const isRewardDeal = [PaymentCurrencyType.Ep, PaymentCurrencyType.Rp].includes(dealCurrency)
    const isTTDeal = dealCurrency === PaymentCurrencyType.Tt

    const products = deal.productConfig?.products ?? []
    const productsLength = products.length
    const price = products[0]?.salePrice?.value

    return {
      load,
      deal,
      /**
       * the underlying currency of the products in the deal, you likely don't
       * want this and should favour the `is*Deal` helpers for logic or
       * `displayCurrency` for displaying prices to a user
       */
      dealCurrency,
      /** The display price of a given deal, in the case of multiple products it will show the cheapest product */
      dealPrice: price,
      /** The currency of the price to show to a user, taking pointsBlend into consideration */
      displayCurrency,
      /** is the underlying deal priced in a fiat currency like GBP */
      isCashDeal: !isPointsPurchase,
      /** is the underlying deal a TT or RP deal, and the user is alible for pointsBlend */
      isPointsBlendDeal,
      /** is this deal a TT or RP deal, i.e not a cash deal */
      isPointsDeal: isPointsPurchase,
      /** @deprecated This should only be used for supporting old clients not seeing points blend deals */
      isRewardDeal: !isPointsBlendDeal && isRewardDeal,
      /** @deprecated This should only be used for supporting old clients not seeing points blend deals */
      isTTDeal: !isPointsBlendDeal && isTTDeal,
      productCount: productsLength,
      walletForDeal: {
        currency: displayCurrency,
        amount: !isPointsPurchase
          ? cashWallet
          : showPointsBlend
            ? data?.walletBlend.amount
            : dealCurrency === PaymentCurrencyType.Tt
              ? data?.walletFP.amount
              : data?.walletRP.amount
      }
    }
  }, [isPointsBlendDeal, data, dealCurrency, cashWalletData, deal, isPointsPurchase, showPointsBlend, load])
}

function useCashWallet({fetchPolicy, isPointsBlendDeal}: Props & {isPointsBlendDeal: boolean}) {
  const [skipCash, setSkipCash] = React.useState(true)

  const {data} = useWalletCashQuery({fetchPolicy, skip: skipCash})
  React.useEffect(() => {
    if (!isPointsBlendDeal) {
      setSkipCash(false)
    }
  }, [isPointsBlendDeal])
  return data
}
